// Owner Payout Ledger PDF — white, print-friendly corporate statement
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  BRAND, rgb, fmtINR,
} from './shared';

const MARGIN = 20;
const INK = '#000000';
const GRAY_44 = '#444444';
const GRAY_66 = '#666666';
const GRAY_88 = '#888888';
const LINE_DD = '#dddddd';
const LINE_EE = '#eeeeee';
const ROW_ALT = '#f9f9f9';
const GOLD = BRAND.GOLD || '#C9A84C';

function haystack(tx) {
  return `${tx?.description || ''} ${tx?.reference || ''}`.toLowerCase();
}

function idAfterColon(ref, prefix) {
  const raw = String(ref || '').trim();
  const needle = `${prefix}:`;
  const idx = raw.toLowerCase().indexOf(needle.toLowerCase());
  if (idx < 0) return '';
  return raw.slice(idx + needle.length).split(/[\s]/)[0].replace(/:held$/i, '').trim();
}

function cleanDesc(raw = '') {
  return String(raw || '').replace(/^\[.*?\]\[.*?\]\s*/, '').trim();
}

function humanLabel(tx) {
  const hay = haystack(tx);
  const stripped = cleanDesc(tx?.description);
  const ref = String(tx?.reference || '');

  if (hay.includes('wallet_recharge') && hay.includes('admin_correction')) {
    return 'Admin Correction';
  }
  if (hay.includes('seed') && hay.includes('reset')) {
    return 'Wallet Reset';
  }
  if (hay.includes('owner_settlement') && hay.includes('contract_termination_release')) {
    return 'Contract Termination Fee';
  }
  if (hay.includes('owner_settlement') && hay.includes('ra_bill_release')) {
    return 'RA-Bill Release';
  }
  if (hay.includes('owner_settlement') && hay.includes('mob_loading_release')) {
    const contractId = idAfterColon(ref, 'mob_loading_release') || '—';
    return `Mobilization Release · ${contractId}`;
  }
  if (hay.includes('settlement_credit')) {
    const bookingId = idAfterColon(ref, 'settlement_credit') || '—';
    return `Booking Settlement · ${bookingId}`;
  }
  if (hay.includes('manual_topup') || hay.includes('wallet_recharge')) {
    return 'Wallet Top-up';
  }
  if (!stripped) return '—';
  return stripped.length > 60 ? `${stripped.slice(0, 60)}…` : stripped;
}

function monthName(year, month) {
  const d = new Date(Date.UTC(Number(year) || 2026, (Number(month) || 1) - 1, 1));
  return d.toLocaleString('en-IN', { month: 'long', timeZone: 'UTC' });
}

function formatDateIst(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || '';
  return `${get('day')}/${get('month')}/${get('year')}`;
}

function signedAmount(tx) {
  const isCredit = String(tx?.type || '').toLowerCase() === 'credit';
  const amt = Number(tx?.amount || 0);
  return `${isCredit ? '+' : '-'}${fmtINR(amt)}`;
}

export async function generateOwnerPayoutLedgerPDF(data = {}) {
  const year = Number(data.year) || new Date().getFullYear();
  const month = Number(data.month) || 1;
  const ownerName = String(data.ownerName || 'Owner').trim() || 'Owner';
  const gstin = String(data.gstin || '').trim() || 'GSTIN Pending';
  const cin = String(data.cin || '').trim() || 'CIN Pending';
  const openingBalance = Number(data.openingBalance || 0);
  const closingBalance = Number(data.closingBalance || 0);
  const creditsTotal = Number(data.creditsTotal || 0);
  const debitsTotal = Number(data.debitsTotal || 0);
  const transactions = Array.isArray(data.transactions) ? data.transactions : [];

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();
  const right = w - MARGIN;
  const contentW = w - MARGIN * 2;

  const paintPage = () => {
    doc.setFillColor(255, 255, 255);
    doc.rect(0, 0, w, h, 'F');
  };

  const drawFooter = () => {
    doc.setDrawColor(...rgb(LINE_DD));
    doc.setLineWidth(0.2);
    doc.line(MARGIN, h - 18, right, h - 18);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...rgb(GRAY_88));
    doc.text('System-generated statement · Not a tax invoice', MARGIN, h - 13);
    doc.text('Development Express · MachineOS Platform', right, h - 13, { align: 'right' });
  };

  paintPage();

  let y = MARGIN + 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.setTextColor(...rgb(GOLD));
  doc.text('DEVELOPMENT EXPRESS', MARGIN, y);

  doc.setTextColor(...rgb(INK));
  doc.setFontSize(14);
  doc.text('PAYOUT LEDGER', right, y, { align: 'right' });

  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...rgb(GRAY_44));
  doc.text('Est. 2011 · Karad, Maharashtra', MARGIN, y);
  doc.setFontSize(9);
  doc.text(`Period: ${monthName(year, month)} ${year}`, right, y, { align: 'right' });

  y += 5;
  doc.setFontSize(8);
  doc.text(`GST: ${gstin} · CIN: ${cin}`, MARGIN, y);
  doc.setFontSize(9);
  doc.text(`Generated: ${formatDateIst(new Date().toISOString())}`, right, y, { align: 'right' });

  y += 6;
  doc.setDrawColor(...rgb(INK));
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, right, y);

  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...rgb(GRAY_66));
  doc.text('Owner Name', MARGIN, y);
  doc.text('Opening Balance', right, y, { align: 'right' });
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...rgb(INK));
  doc.text(ownerName, MARGIN, y);
  doc.text(fmtINR(openingBalance), right, y, { align: 'right' });

  y += 5;
  doc.setDrawColor(...rgb(LINE_DD));
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y, right, y);

  autoTable(doc, {
    startY: y + 4,
    margin: { left: MARGIN, right: MARGIN, top: MARGIN, bottom: 28 },
    head: [['DATE', 'DESCRIPTION', 'AMOUNT', 'TYPE']],
    body: transactions.map((tx) => {
      const isCredit = String(tx.type || '').toLowerCase() === 'credit';
      return [
        formatDateIst(tx.created_at),
        humanLabel(tx),
        signedAmount(tx),
        isCredit ? 'Credit' : 'Debit',
      ];
    }),
    styles: {
      font: 'helvetica',
      fontSize: 10,
      textColor: rgb(INK),
      fillColor: rgb('#ffffff'),
      lineColor: rgb(LINE_EE),
      lineWidth: 0.15,
      cellPadding: 2.4,
    },
    headStyles: {
      fillColor: rgb('#000000'),
      textColor: rgb('#ffffff'),
      fontStyle: 'bold',
      fontSize: 9,
      cellPadding: 2.6,
    },
    alternateRowStyles: {
      fillColor: rgb(ROW_ALT),
    },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 'auto' },
      2: { cellWidth: 32, halign: 'right', fontStyle: 'bold' },
      3: { cellWidth: 20, halign: 'center' },
    },
    didDrawPage: (hook) => {
      if (hook.pageNumber > 1) paintPage();
      drawFooter();
    },
  });

  y = (doc.lastAutoTable && doc.lastAutoTable.finalY ? doc.lastAutoTable.finalY : y) + 8;
  const boxH = 28;
  if (y + boxH > h - 28) {
    doc.addPage();
    paintPage();
    drawFooter();
    y = MARGIN;
  }

  const boxTop = y;
  doc.setDrawColor(...rgb(INK));
  doc.setLineWidth(0.3);
  doc.rect(MARGIN, boxTop, contentW, boxH);

  const row1 = boxTop + 7;
  const row2 = boxTop + 14;
  const row3 = boxTop + 23;
  const padX = MARGIN + 4;
  const padR = right - 4;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...rgb(INK));
  doc.text('Total Credits', padX, row1);
  doc.text(fmtINR(creditsTotal), padR, row1, { align: 'right' });
  doc.text('Total Debits', padX, row2);
  doc.text(fmtINR(debitsTotal), padR, row2, { align: 'right' });

  doc.setLineWidth(0.5);
  doc.line(MARGIN, boxTop + 17, right, boxTop + 17);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Closing Balance', padX, row3);
  doc.text(fmtINR(closingBalance), padR, row3, { align: 'right' });

  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    doc.setPage(i);
    drawFooter();
  }

  const filename = `payout-ledger-${year}-${String(month).padStart(2, '0')}.pdf`;
  doc.save(filename);
  doc.__deFilename = filename;
  return doc;
}
